import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LeaderboardMenu.css';
import { useAuth } from '../utils/AuthContext';
import { useAudio } from '../utils/AudioContext'; 
import { 
    playUiHoverSound, 
    playUiConfirmSound, 
    playUiClickSound, // 1. Import Click Sound
    playUiOpenSound, 
    playUiCloseSound 
} from '../utils/sounds'; 
import { db } from '../../firebase';
import { 
    ref, query, orderByChild, limitToLast, get, 
    endAt
} from 'firebase/database';
import { 
    Trophy, Users, Globe, X, 
    Crown, Medal, Search, User,
    Save, LayoutGrid, LogIn 
} from 'lucide-react';
import { AVATARS } from './AccountMenu';

// Constants
const PAGE_SIZE = 20;

// --- Date Helpers ---
const getWeekId = () => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(),0,1);
    const weekNo = Math.ceil(( ( (d - yearStart) / 86400000) + 1)/7);
    return `${d.getFullYear()}_${weekNo}`;
};

const getMonthId = () => {
    const d = new Date();
    return `${d.getFullYear()}_${d.getMonth() + 1}`;
};

const getLeaderboardPath = (tab) => {
    if (tab === 'weekly') return `leaderboards/weekly_${getWeekId()}`;
    if (tab === 'monthly') return `leaderboards/monthly_${getMonthId()}`;
    return `leaderboards/${tab}`; // 'alltime'
};

const LeaderboardMenu = ({ onClose, onOpenLogin }) => {
    const { currentUser } = useAuth();
    const { sfxMuted } = useAudio(); 
    
    // UI State
    const [activeTab, setActiveTab] = useState('alltime'); 
    const [filterMode, setFilterMode] = useState('global'); 
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [myEntry, setMyEntry] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [myRank, setMyRank] = useState('---'); 
    
    // Animation State
    const [isExiting, setIsExiting] = useState(false);
    const [exitType, setExitType] = useState(null); 
    
    // Pagination State
    const [lastDoc, setLastDoc] = useState(null); 
    
    const showAuthPrompt = !currentUser;
    const observer = useRef();

    // Play Open Sound on Mount
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle closing with animation
    const handleClose = useCallback(() => {
        playUiCloseSound(sfxMuted); 
        setExitType('close');
        setIsExiting(true);
    }, [sfxMuted]);

    const handleLoginClick = () => {
        playUiConfirmSound(sfxMuted); 
        setExitType('login');
        setIsExiting(true);
    };

    const handleAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return;

        if (isExiting) {
            if (exitType === 'login') {
                onOpenLogin();
            } else {
                onClose();
            }
        }
    };

    // --- FETCH FUNCTIONS ---

    const fetchGlobalLeaderboard = useCallback(async (isReset, path) => {
        const lbRef = ref(db, path);
        let q;
        const cursor = isReset ? null : lastDoc;

        if (!cursor) {
            q = query(lbRef, orderByChild('score'), limitToLast(PAGE_SIZE));
        } else {
            q = query(
                lbRef, 
                orderByChild('score'), 
                endAt(cursor.score, cursor.key), 
                limitToLast(PAGE_SIZE + 1)
            );
        }

        const snapshot = await get(q);
        
        if (snapshot.exists()) {
            const data = snapshot.val();
            let sortedData = Object.keys(data).map(key => ({
                uid: key,
                ...data[key]
            })).sort((a, b) => b.score - a.score);

            if (!isReset && cursor) {
                sortedData = sortedData.filter(item => item.uid !== cursor.key);
            }

            if (sortedData.length < PAGE_SIZE) setHasMore(false);

            if (sortedData.length > 0) {
                const lastItem = sortedData[sortedData.length - 1];
                setLastDoc({ score: lastItem.score, key: lastItem.uid });
                setEntries(prev => isReset ? sortedData : [...prev, ...sortedData]);
            } else {
                setHasMore(false);
            }
        } else {
            setHasMore(false);
            if (isReset) setEntries([]);
        }
    }, [lastDoc]);

    const fetchFriendsLeaderboard = useCallback(async (path) => {
        if (!currentUser) return;
        const friendsRef = ref(db, `users/${currentUser.uid}/friends`);
        const friendsSnap = await get(friendsRef);
        
        let friendsList = [];
        if (friendsSnap.exists()) {
            friendsList = Object.keys(friendsSnap.val());
        }
        
        friendsList.push(currentUser.uid);

        const promises = friendsList.map(fid => get(ref(db, `${path}/${fid}`)));
        const snapshots = await Promise.all(promises);
        
        const friendEntries = [];
        snapshots.forEach((snap, index) => {
            if (snap.exists()) {
                friendEntries.push({ uid: friendsList[index], ...snap.val() });
            }
        });

        friendEntries.sort((a, b) => b.score - a.score);
        
        setEntries(friendEntries);
        setHasMore(false);
    }, [currentUser]);

    // Fetch Logic
    const fetchData = useCallback(async (isReset = false, currentTab = activeTab) => {
        if (!currentUser) return;
        setLoading(true);

        try {
            const path = getLeaderboardPath(currentTab);

            if (filterMode === 'friends') {
                await fetchFriendsLeaderboard(path);
            } else {
                await fetchGlobalLeaderboard(isReset, path);
            }
            
            // Fetch self entry for the footer
            const myScoreRef = ref(db, `${path}/${currentUser.uid}`);
            const snapshot = await get(myScoreRef);
            if (snapshot.exists()) {
                setMyEntry({ uid: currentUser.uid, ...snapshot.val() });
            } else {
                setMyEntry(null);
            }

        } catch (err) {
            console.error("Error fetching leaderboard:", err);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filterMode, currentUser, fetchFriendsLeaderboard, fetchGlobalLeaderboard]);


    // Reset state and fetch when tab or filter changes
    useEffect(() => {
        if (!currentUser) return;
        
        setEntries([]);
        setLastDoc(null);
        setHasMore(true);
        setLoading(true);
        setMyRank('---');
        
        fetchData(true, activeTab);

        if (filterMode === 'global') {
            fetchGlobalRank(activeTab);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filterMode, currentUser]);

    // Calculate Global Rank
    const fetchGlobalRank = async (currentTab) => {
        try {
            const path = getLeaderboardPath(currentTab);
            const lbRef = ref(db, path);
            const snapshot = await get(lbRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                const sorted = Object.keys(data)
                    .map(key => ({ uid: key, score: data[key].score }))
                    .sort((a, b) => b.score - a.score);
                
                const rank = sorted.findIndex(u => u.uid === currentUser.uid) + 1;
                setMyRank(rank > 0 ? `#${rank}` : 'Unranked');
            } else {
                setMyRank('Unranked');
            }
        } catch (err) {
            console.error("Error calculating rank", err);
            setMyRank('---');
        }
    };

    // Infinite Scroll Ref
    const lastElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && filterMode === 'global') {
                fetchData(false, activeTab);
            }
        }, { rootMargin: '400px' });
        
        if (node) observer.current.observe(node);
    }, [loading, hasMore, filterMode, fetchData, activeTab]);

    // Helpers
    const getAvatarIcon = (id) => {
        const avatar = AVATARS.find(a => a.id === id);
        return avatar ? avatar.icon : <User />;
    };

    const getAvatarColor = (id) => {
        const avatar = AVATARS.find(a => a.id === id);
        return avatar ? avatar.color : '#ffffff';
    };

    const getRankStyle = (index) => {
        if (index === 0) return { color: '#fbbf24', icon: <Crown size={18} fill="#fbbf24" /> };
        if (index === 1) return { color: '#94a3b8', icon: <Medal size={18} fill="#94a3b8" /> };
        if (index === 2) return { color: '#b45309', icon: <Medal size={18} fill="#b45309" /> };
        return { color: '#fff', icon: <span className="rank-number">#{index + 1}</span> };
    };

    // --- ENHANCED AUTH PROMPT RENDER ---
    if (showAuthPrompt) {
        return (
            <div className={`leaderboard-overlay ${isExiting ? 'fade-out' : 'fade-in'}`}>
                <div 
                    className={`leaderboard-modal auth-prompt-modal enhanced ${isExiting ? 'pop-out' : ''}`}
                    onAnimationEnd={handleAnimationEnd}
                >
                    <div className="auth-header">
                        <div className="icon-glow-container">
                            <Trophy size={48} className="c-yellow" />
                        </div>
                        <h2>Join the Ranks</h2>
                        <p className="subtitle">Sync your progress and compete with the world!</p>
                    </div>

                    <div className="feature-grid">
                        <div className="feature-item">
                            <div className="f-icon bg-blue"><Globe size={20} /></div>
                            <div className="f-text">
                                <span>Global Ranking</span>
                                <small>See where you stand</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-green"><Save size={20} /></div>
                            <div className="f-text">
                                <span>Cloud Save</span>
                                <small>Never lose your highscore</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-purple"><LayoutGrid size={20} /></div>
                            <div className="f-text">
                                <span>Custom Avatar</span>
                                <small>Stand out on the board</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-orange"><Users size={20} /></div>
                            <div className="f-text">
                                <span>Friends</span>
                                <small>Challenge your rivals</small>
                            </div>
                        </div>
                    </div>

                    <div className="auth-actions">
                        <button 
                            onClick={handleLoginClick} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            className="btn btn-login-pulse"
                        >
                            <LogIn size={20} />
                            <span>Sign In / Register</span>
                        </button>
                        <button 
                            onClick={handleClose} 
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            className="btn-text-only"
                        >
                            Maybe Later
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const footerRankDisplay = filterMode === 'friends' 
        ? (entries.length > 0 ? `#${entries.findIndex(e => e.uid === currentUser.uid) + 1}` : '...') 
        : myRank;

    return (
        <div className={`leaderboard-overlay ${isExiting ? 'fade-out' : 'fade-in'}`}>
            <div 
                className={`leaderboard-modal ${isExiting ? 'pop-out' : ''}`}
                onAnimationEnd={handleAnimationEnd}
            >
                <div className="lb-header">
                    <div className="lb-title-group">
                        <Trophy className="c-yellow" size={32} />
                        <h1>LEADERBOARD</h1>
                    </div>
                    <button 
                        className="close-btn" 
                        onClick={handleClose}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        <X />
                    </button>
                </div>

                <div className="lb-controls">
                    <div className="lb-tabs">
                        {['weekly', 'monthly', 'alltime'].map(tab => (
                            <button 
                                key={tab}
                                className={`lb-tab ${activeTab === tab ? 'active' : ''}`}
                                onClick={() => {
                                    playUiClickSound(sfxMuted); // 2. Use Click Sound
                                    setActiveTab(tab);
                                }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                {tab.replace('alltime', 'All Time')}
                            </button>
                        ))}
                    </div>
                    
                    <div className="lb-filter-toggle">
                        <button 
                            className={`filter-btn ${filterMode === 'global' ? 'active' : ''}`}
                            onClick={() => {
                                playUiClickSound(sfxMuted); // 3. Use Click Sound
                                setFilterMode('global');
                            }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            <Globe size={16} /> Global
                        </button>
                        <button 
                            className={`filter-btn ${filterMode === 'friends' ? 'active' : ''}`}
                            onClick={() => {
                                playUiClickSound(sfxMuted); // 4. Use Click Sound
                                setFilterMode('friends');
                            }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            <Users size={16} /> Friends
                        </button>
                    </div>
                </div>

                <div className="lb-list-container">
                    <div className="lb-list-header">
                        <span className="col-rank">Rank</span>
                        <span className="col-player">Player</span>
                        <span className="col-score">Score</span>
                    </div>

                    <div className="lb-list-content custom-scroll">
                        {entries.map((entry, index) => {
                            const rankData = getRankStyle(index);
                            const isMe = entry.uid === currentUser.uid;

                            return (
                                <div 
                                    key={`${entry.uid}-${index}`} 
                                    ref={index === entries.length - 5 ? lastElementRef : null}
                                    className={`lb-item ${isMe ? 'is-me' : ''} slide-in-bottom`}
                                    style={{animationDelay: `${(index % 20) * 0.05}s`}}
                                >
                                    <div className="col-rank" style={{ color: rankData.color }}>
                                        {rankData.icon}
                                    </div>
                                    <div className="col-player">
                                        <div 
                                            className="lb-avatar"
                                            style={{ 
                                                borderColor: getAvatarColor(entry.avatarId),
                                                color: getAvatarColor(entry.avatarId)
                                            }}
                                        >
                                            {getAvatarIcon(entry.avatarId)}
                                        </div>
                                        <div className="player-info">
                                            <span className="player-name">{entry.username}</span>
                                            {isMe && <span className="me-tag">YOU</span>}
                                        </div>
                                    </div>
                                    <div className="col-score">{entry.score}</div>
                                </div>
                            );
                        })}
                        
                        {loading && (
                            <div className="loading-spinner-container">
                                <div className="spinner"></div>
                            </div>
                        )}
                        
                        {!loading && entries.length === 0 && (
                            <div className="empty-state">
                                <Search size={48} className="c-grey" />
                                <p>No records found yet.</p>
                            </div>
                        )}
                    </div>
                </div>

                {myEntry && (
                    <div className="lb-footer slide-up">
                        <div className="lb-item footer-item is-me">
                            <div className="col-rank">
                                <span className="rank-number">{footerRankDisplay}</span>
                            </div>
                            <div className="col-player">
                                <div 
                                    className="lb-avatar"
                                    style={{ 
                                        borderColor: getAvatarColor(myEntry.avatarId),
                                        color: getAvatarColor(myEntry.avatarId)
                                    }}
                                >
                                    {getAvatarIcon(myEntry.avatarId)}
                                </div>
                                <span className="player-name">{myEntry.username} (You)</span>
                            </div>
                            <div className="col-score">{myEntry.score}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaderboardMenu;